/** Browser side of the Spurt SFU protocol (media/interop/client.html is the reference):
 *  one RTCPeerConnection, a data channel "signal" created before the first offer, the offer POSTed to the join URL with
 *  the join token, the server's later offers answered over the channel, simulcast rids l/m/h for camera video. */

export interface RosterPeer {
  stream: string;
  user: string;
  name: string;
  muted: boolean;
  role: string;
}

export type EndReason = "left" | "kicked" | "ended" | "failed";

export interface JoinInfo {
  url: string;
  token: string;
  /** the media server answers null when no TURN server is configured; join() treats that as [] */
  iceServers?: RTCIceServer[] | null;
}

/** One remote person as the UI needs them.
 *
 *  The server gives no flag for "this video track is a screen share" (docs/MEETINGS_CONTRACT.md section 6, gap 4), so this
 *  is a heuristic. Each roster peer names one MediaStream id (`RosterPeer.stream`). Inside that stream the first live video
 *  track is taken to be the camera and a second live video track is taken to be a screen share. That matches how the SFU
 *  labels tracks (first video = camera, next = screen). Known limit: a person who joined without any camera and then shares
 *  their screen publishes a single video track, which is shown as their camera tile and does not trigger the presenting layout. */
export interface RemotePeer {
  peer: RosterPeer;
  /** the raw stream (audio plus every video track); use it for audio playback */
  stream: MediaStream | null;
  /** audio-free stream holding the first live video track, for the person's tile */
  camera: MediaStream | null;
  /** stream holding the second live video track: a screen share */
  screen: MediaStream | null;
}

export interface RemoteMap {
  peers: RemotePeer[];
  /** streams with a live video track that no roster peer claims (yet). The UI shows these as a shared screen of an unknown person only once a roster has arrived. */
  unmatched: MediaStream[];
}

/** Pure mapping of roster peers and received streams (see RemotePeer for the heuristic). `cache` keeps MediaStream identities stable between calls so React does not re-attach video elements. */
export function mapRemotes(peers: RosterPeer[], streams: MediaStream[], cache: Map<string, MediaStream> = new Map()): RemoteMap {
  const byId = new Map(streams.map((s) => [s.id, s] as const));
  const claimed = new Set<string>();
  const single = (t: MediaStreamTrack, tag: string): MediaStream => {
    const k = tag + t.id;
    let s = cache.get(k);
    if (!s) {
      s = new MediaStream([t]);
      cache.set(k, s);
    }
    return s;
  };
  const out: RemotePeer[] = peers.map((peer) => {
    const stream = byId.get(peer.stream) ?? null;
    if (!stream) return { peer, stream: null, camera: null, screen: null };
    claimed.add(stream.id);
    const vids = stream.getVideoTracks().filter((t) => t.readyState === "live");
    const cam = vids[0];
    const scr = vids[1];
    return { peer, stream, camera: cam ? single(cam, "cam:") : null, screen: scr ? single(scr, "scr:") : null };
  });
  const unmatched = streams.filter((s) => !claimed.has(s.id) && s.getVideoTracks().some((t) => t.readyState === "live"));
  // forget cached wrappers whose track is gone
  const alive = new Set(streams.flatMap((s) => s.getVideoTracks().map((t) => t.id)));
  for (const k of [...cache.keys()]) if (!alive.has(k.slice(4))) cache.delete(k);
  return { peers: out, unmatched };
}

export interface SfuEvents {
  onRoster(peers: RosterPeer[]): void;
  onStreams(streams: MediaStream[]): void;
  onEnd(reason: EndReason): void;
  /** the server muted (or allowed) one of your tracks: data-channel message `muted {kind,muted,by}` */
  onMuted?(kind: "audio" | "video", muted: boolean, by: string): void;
  /** the active speaker changed: `speaker {user}` (user may be empty when nobody speaks) */
  onSpeaker?(user: string): void;
  /** the server changed your receive quality: `quality {level}`; the level's scale is not documented, so it is passed through */
  onQuality?(level: unknown): void;
  /** your own screen share started (true) or stopped (false), including when the browser's own "Stop sharing" bar ends it */
  onScreenShare?(sharing: boolean): void;
  /** roster plus streams, joined; fired whenever either changes */
  onRemotes?(map: RemoteMap): void;
}

/** Browser calls the session makes outside the peer connection; replaceable for tests. */
export interface SfuIo {
  getDisplayMedia?: () => Promise<MediaStream>;
  getUserMedia?: (c: MediaStreamConstraints) => Promise<MediaStream>;
}

const ANSWER_TIMEOUT_MS = 10_000;

export class JoinError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "JoinError";
  }
}

export class SfuSession {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private local: MediaStream | null = null;
  private remote = new Map<string, MediaStream>();
  private ended = false;
  private disconnectTimer: number | undefined;
  private peers: RosterPeer[] = [];
  private rosterSeen = false;
  private readonly wrappers = new Map<string, MediaStream>();
  private screen: { track: MediaStreamTrack; onEnded: () => void } | null = null;
  private screenTr: RTCRtpTransceiver | null = null;
  private neg: Promise<void> = Promise.resolve();
  private waiter: { resolve: () => void; reject: (e: Error) => void; timer: number } | null = null;

  constructor(
    private readonly ev: SfuEvents,
    private readonly mk: (cfg: RTCConfiguration) => RTCPeerConnection = (c) => new RTCPeerConnection(c),
    private readonly io: SfuIo = {},
  ) {}

  get localStream(): MediaStream | null {
    return this.local;
  }

  /** true while your own screen is being shared */
  get sharingScreen(): boolean {
    return this.screen !== null;
  }

  /** true when there is a live camera track to send */
  get hasCamera(): boolean {
    return (this.local?.getVideoTracks() ?? []).some((t) => t.readyState === "live");
  }

  /** true once the server has sent at least one roster */
  get hasRoster(): boolean {
    return this.rosterSeen;
  }

  async join(info: JoinInfo, media: MediaStream | null, withVideo: boolean): Promise<void> {
    this.local = media;
    const pc = (this.pc = this.mk({ iceServers: info.iceServers ?? [], bundlePolicy: "max-bundle" }));
    pc.ontrack = (e) => {
      const s = e.streams[0] ?? new MediaStream([e.track]);
      if (!this.remote.has(s.id)) s.addEventListener("removetrack", () => this.refresh());
      this.remote.set(s.id, s);
      e.track.addEventListener("ended", () => this.refresh());
      this.refresh();
    };
    pc.onconnectionstatechange = () => {
      window.clearTimeout(this.disconnectTimer);
      if (pc.connectionState === "failed") this.finish("failed");
      else if (pc.connectionState === "disconnected") this.disconnectTimer = window.setTimeout(() => this.finish("failed"), 10_000);
    };
    const dc = (this.dc = pc.createDataChannel("signal"));
    dc.onmessage = (e) => void this.onSignal(String(e.data));

    if (media) {
      for (const t of media.getAudioTracks()) pc.addTransceiver(t, { direction: "sendonly", streams: [media] });
      if (withVideo) {
        for (const t of media.getVideoTracks()) {
          pc.addTransceiver(t, {
            direction: "sendonly",
            streams: [media],
            // libwebrtc treats the first encoding as the lowest layer, so list low to high
            sendEncodings: [
              { rid: "l", maxBitrate: 150_000, scaleResolutionDownBy: 4 },
              { rid: "m", maxBitrate: 500_000, scaleResolutionDownBy: 2 },
              { rid: "h", maxBitrate: 1_500_000, scaleResolutionDownBy: 1 },
            ],
          });
        }
      }
    }
    // always be able to receive both, even if we send nothing
    if (!media || media.getAudioTracks().length === 0) pc.addTransceiver("audio", { direction: "recvonly" });
    if (withVideo && (!media || media.getVideoTracks().length === 0)) pc.addTransceiver("video", { direction: "recvonly" });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    let res: Response;
    try {
      res = await fetch(info.url, {
        method: "POST",
        headers: { Authorization: `Bearer ${info.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ type: pc.localDescription?.type, sdp: pc.localDescription?.sdp }),
        credentials: "omit",
      });
    } catch {
      this.finish("failed", false);
      throw new JoinError("Can't reach the call server", 0);
    }
    if (!res.ok) {
      this.finish("failed", false);
      throw new JoinError(res.status === 401 || res.status === 403 ? "The call link has expired" : "The call server refused the connection", res.status);
    }
    await pc.setRemoteDescription((await res.json()) as RTCSessionDescriptionInit);
  }

  private refresh() {
    const live = [...this.remote.values()].filter((s) => s.getTracks().some((t) => t.readyState === "live"));
    this.ev.onStreams(live);
    this.emitRemotes(live);
  }

  private emitRemotes(live?: MediaStream[]): void {
    if (!this.ev.onRemotes) return;
    const streams = live ?? [...this.remote.values()].filter((s) => s.getTracks().some((t) => t.readyState === "live"));
    this.ev.onRemotes(mapRemotes(this.peers, streams, this.wrappers));
  }

  private async onSignal(raw: string): Promise<void> {
    const pc = this.pc;
    if (!pc || this.ended) return;
    let m: { type?: string; sdp?: string; peers?: RosterPeer[]; kind?: string; muted?: boolean; by?: string; user?: string; level?: unknown };
    try {
      m = JSON.parse(raw);
    } catch {
      return;
    }
    try {
      if (m.type === "offer" && m.sdp) {
        // the server is polite: ignore its offer while ours is in flight
        if (pc.signalingState !== "stable") return;
        await pc.setRemoteDescription({ type: "offer", sdp: m.sdp });
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        this.dc?.send(JSON.stringify({ type: "answer", sdp: pc.localDescription?.sdp }));
      } else if (m.type === "answer" && m.sdp) {
        if (pc.signalingState === "have-local-offer") {
          await pc.setRemoteDescription({ type: "answer", sdp: m.sdp });
          this.settle();
        }
      } else if (m.type === "roster" && Array.isArray(m.peers)) {
        this.peers = m.peers;
        this.rosterSeen = true;
        this.ev.onRoster(m.peers);
        this.emitRemotes();
      } else if (m.type === "muted") {
        this.ev.onMuted?.(m.kind === "video" ? "video" : "audio", m.muted !== false, String(m.by ?? ""));
      } else if (m.type === "speaker") {
        this.ev.onSpeaker?.(String(m.user ?? ""));
      } else if (m.type === "quality") {
        this.ev.onQuality?.(m.level);
      } else if (m.type === "kicked" || m.type === "ended") {
        this.finish(m.type);
      }
    } catch (e) {
      console.warn("signal handling failed", e);
      this.settle(e instanceof Error ? e : new Error("The call server's answer could not be applied"));
    }
  }

  /** Resolve (or fail) the renegotiation that is waiting for the server's answer. */
  private settle(err?: Error): void {
    const w = this.waiter;
    if (!w) return;
    this.waiter = null;
    window.clearTimeout(w.timer);
    if (err) w.reject(err);
    else w.resolve();
  }

  /** Client-initiated renegotiation over the "signal" channel: offer out, the server's answer in. Calls are serialised. */
  private renegotiate(): Promise<void> {
    const run = async (): Promise<void> => {
      const pc = this.pc;
      const dc = this.dc;
      if (!pc || !dc || this.ended) throw new Error("You are not connected to the call");
      if (dc.readyState !== "open") throw new Error("The call connection is not ready yet");
      for (let i = 0; i < 100 && pc.signalingState !== "stable"; i++) await new Promise((r) => window.setTimeout(r, 50));
      if (pc.signalingState !== "stable") throw new Error("The call connection is busy, try again");
      await pc.setLocalDescription(await pc.createOffer());
      const answered = new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          this.waiter = null;
          reject(new Error("The call server did not answer"));
        }, ANSWER_TIMEOUT_MS);
        this.waiter = { resolve, reject, timer };
      });
      dc.send(JSON.stringify({ type: "offer", sdp: pc.localDescription?.sdp }));
      try {
        await answered;
      } catch (e) {
        try {
          if ((pc.signalingState as string) === "have-local-offer") await pc.setLocalDescription({ type: "rollback" });
        } catch {
          /* nothing to roll back */
        }
        throw e;
      }
    };
    const next = this.neg.then(run, run);
    this.neg = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  /** Share your screen: getDisplayMedia, a second sendonly video transceiver, renegotiate, then tell the SFU the track is a screen
   *  (contract section 6). Throws if the person cancels the picker or the server does not answer. A blocked share is dropped silently
   *  by the SFU, so callers should check JoinOut.caps.pubScreen first. */
  async startScreen(): Promise<void> {
    const pc = this.pc;
    const dc = this.dc;
    if (!pc || !dc || this.ended) throw new Error("You are not connected to the call");
    if (this.screen) return;
    const getDisplay = this.io.getDisplayMedia ?? (() => navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15 } }, audio: false }));
    const stream = await getDisplay();
    const track = stream.getVideoTracks()[0];
    if (!track) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error("No screen was selected");
    }
    try {
      (track as { contentHint?: string }).contentHint = "detail";
    } catch {
      /* optional hint */
    }
    let tr = this.screenTr;
    try {
      if (tr) {
        await tr.sender.replaceTrack(track);
        tr.direction = "sendonly";
      } else {
        tr = pc.addTransceiver(track, { direction: "sendonly", streams: [stream] });
        this.screenTr = tr;
      }
      await this.renegotiate();
      const mid = tr.mid;
      if (!mid) throw new Error("The call server did not accept the screen share");
      dc.send(JSON.stringify({ type: "source", mid, source: "screen" }));
    } catch (e) {
      track.stop();
      try {
        if (tr && this.screenTr === tr) {
          pc.removeTrack(tr.sender);
        }
      } catch {
        /* already removed */
      }
      throw e;
    }
    const onEnded = () => void this.stopScreen();
    track.addEventListener("ended", onEnded);
    this.screen = { track, onEnded };
    this.ev.onScreenShare?.(true);
  }

  /** Stop sharing (also what runs when the browser's own "Stop sharing" bar ends the track). Never throws. */
  async stopScreen(): Promise<void> {
    const s = this.screen;
    if (!s) return;
    this.screen = null;
    s.track.removeEventListener("ended", s.onEnded);
    s.track.stop();
    this.ev.onScreenShare?.(false);
    const pc = this.pc;
    if (!pc || this.ended) return;
    try {
      if (this.screenTr) pc.removeTrack(this.screenTr.sender);
      await this.renegotiate();
    } catch (e) {
      console.warn("could not renegotiate after stopping the screen share", e);
    }
  }

  /** Start the camera after joining without one: a new sendonly video transceiver and a renegotiation. If a live camera track
   *  already exists this only enables it. Single layer (no simulcast), because encodings cannot be added after the first offer. */
  async startCamera(): Promise<void> {
    if (this.hasCamera) {
      this.setCameraEnabled(true);
      return;
    }
    const pc = this.pc;
    if (!pc || this.ended) throw new Error("You are not connected to the call");
    const getUm = this.io.getUserMedia ?? ((c: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(c));
    const cam = await getUm({ video: mediaConstraints(true).video as MediaTrackConstraints });
    const track = cam.getVideoTracks()[0];
    if (!track) throw new Error("No camera was found");
    const local = this.local ?? new MediaStream();
    try {
      local.addTrack(track);
      pc.addTransceiver(track, { direction: "sendonly", streams: [local] });
      this.local = local;
      await this.renegotiate();
    } catch (e) {
      local.removeTrack(track);
      track.stop();
      throw e;
    }
  }

  setMicEnabled(on: boolean): void {
    this.local?.getAudioTracks().forEach((t) => (t.enabled = on));
  }

  setCameraEnabled(on: boolean): void {
    this.local?.getVideoTracks().forEach((t) => (t.enabled = on));
  }

  /** same as setCameraEnabled */
  setCamEnabled(on: boolean): void {
    this.setCameraEnabled(on);
  }

  leave(): void {
    this.finish("left");
  }

  private finish(reason: EndReason, notify = true): void {
    if (this.ended) return;
    this.ended = true;
    window.clearTimeout(this.disconnectTimer);
    this.settle(new Error("The call ended"));
    if (this.screen) {
      this.screen.track.removeEventListener("ended", this.screen.onEnded);
      this.screen.track.stop();
      this.screen = null;
      this.ev.onScreenShare?.(false);
    }
    try {
      this.dc?.close();
      this.pc?.close();
    } catch {
      /* already closed */
    }
    this.local?.getTracks().forEach((t) => t.stop());
    this.remote.clear();
    if (notify) this.ev.onEnd(reason);
  }
}

export const mediaConstraints = (video: boolean): MediaStreamConstraints => ({
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  video: video ? { width: { ideal: 960 }, height: { ideal: 540 }, frameRate: { ideal: 30 } } : false,
});

/** Turn a getUserMedia failure into something a person can act on. */
export const mediaErrorText = (e: unknown): string => {
  const name = e instanceof DOMException ? e.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "Allow microphone access in your browser to make calls";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No microphone or camera was found";
  if (name === "NotReadableError") return "Your microphone or camera is being used by another app";
  return "Couldn't start your microphone or camera";
};

/** Turn a getDisplayMedia failure into something a person can act on; "" means they simply cancelled. */
export const screenErrorText = (e: unknown): string => {
  const name = e instanceof DOMException ? e.name : "";
  if (name === "NotAllowedError" || name === "AbortError") return "";
  if (name === "NotFoundError") return "There is no screen to share";
  if (name === "NotReadableError") return "Your screen could not be captured";
  return e instanceof Error && e.message ? e.message : "Couldn't share your screen";
};
